


























































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import SortableItem from "./SortableItem.vue";

// Enums
import { SortableItems } from "@/data/enums/sortableItems";

// Types
import {
  ISortableData,
  ISortableCan,
  ISortableOptions
} from "@/models/reordering";
import { IProduct, IProductCategory } from "@/models/products";

@Component({
  name: "sortable-category",
  components: {
    "sortable-item": SortableItem
  }
})
export default class extends Vue {
  // Props
  @Prop({ required: true })
  public sortableElement!: any;

  @Prop({ required: true })
  public sortableItemName!: string;

  @Prop({ required: true })
  public level!: number;

  @Prop({ required: true })
  public scope!: string;

  @Prop({ default: 10 })
  public limit!: number;

  @Prop({ default: false })
  public lockToParent!: boolean;

  @Prop({ required: true })
  public isItemClickable!: boolean;

  @Prop({ required: true })
  public itemName!: string;

  // Optional functionalities
  @Prop({ default: () => null })
  public loadItems!: Function;

  @Prop({ default: () => null })
  public reorder!: Function;

  @Prop({ default: () => null })
  public confirmDetach!: Function;

  @Prop({ default: () => null })
  public createSubcategory!: Function;

  @Prop({ default: () => null })
  public createItem!: Function;

  @Prop({ default: () => null })
  public confirmDelete!: Function;

  @Prop({ default: () => null })
  public view!: Function;

  @Prop({ default: () => null })
  public duplicateItem!: Function;

  @Prop({ default: false })
  public isSelectable!: boolean;

  @Prop({ default: false })
  public isCategorySelectable!: boolean;

  @Prop({ default: true })
  public showCategoryItems!: boolean;

  @Prop({ default: true })
  public showCounters!: boolean;

  @Prop({ default: true })
  public multipleSelect!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: "" })
  public routeName!: string;

  // Variables
  public sortableCategoriesModule = "sortableCategories";

  public sortElems: ISortableData[] = [];
  public savedOrder: ISortableData[] = [];

  public isReordering = false;
  public sortableCategoriesId = this.$uuid();

  public sortableOptions: ISortableOptions = {
    chosenClass: "dragged-item",
    handle: ".drag-and-drop",
    group: {
      name: "sortable-list-2",
      pull: true,
      put: true
    },
    animation: 250,
    fallbackOnBody: true
  };

  public async onDragDrop(movedItemData) {
    this.isReordering = true;
    this.savedOrder = this.$_.cloneDeep(this.sortElems);

    try {
      const result = await this.reorder(movedItemData);

      if (!this.$_.get(result, "ignoreRefresh")) {
        if (movedItemData.itemMoved.type === SortableItems.ITEM) {
          await this.deleteItem(movedItemData.oldCategory, {
            id: movedItemData.itemMoved.id
          });

          await this.pasteItem(movedItemData.newCategory, {
            item: this.$_.merge(
              this.$_.pick(movedItemData.itemMoved, ["id", "itemId", "name"]),
              {
                order: movedItemData.newIndex,
                type: SortableItems.ITEM
              },
              this.$_.has(movedItemData, "itemMoved.labels")
                ? { labels: this.$_.get(movedItemData, "itemMoved.labels") }
                : {}
            ),
            index: movedItemData.newIndex
          });
        } else {
          await this.deleteCategory(movedItemData.itemMoved.id);
          await this.pasteCategory(movedItemData.newCategory, {
            category: movedItemData.itemMoved,
            index: movedItemData.newIndex
          });
        }
      }

      this.$store.dispatch("toast/show", {
        message: this.$t("_.order_updated")
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.refreshList();
    }

    this.isReordering = false;
  }

  public refreshList() {
    this.$emit("refreshList");
  }

  public deleteCategory(categoryId) {
    return this.$store.commit(
      `${this.sortableCategoriesModule}/deleteCategory`,
      {
        scope: this.scope,
        categoryId
      }
    );
  }

  public deleteItem(categoryId, data) {
    return this.$store.commit(`${this.sortableCategoriesModule}/deleteItem`, {
      scope: this.scope,
      categoryId,
      data
    });
  }

  public pasteCategory(categoryId, data) {
    return this.$store.commit(
      `${this.sortableCategoriesModule}/pasteCategory`,
      {
        scope: this.scope,
        categoryId: categoryId,
        data
      }
    );
  }

  public pasteItem(categoryId, data) {
    return this.$store.commit(`${this.sortableCategoriesModule}/pasteItem`, {
      scope: this.scope,
      categoryId,
      data
    });
  }

  public get showLoadAllButton() {
    return this.sortableElement.leftToLoad > this.limit;
  }

  public get getSortableClasses() {
    return `sortable-container is-relative is-level-${
      this.level
    }  is-category-${this.sortableElement.id} loaded-products-${
      this.$_.filter(this.sortElems, ["type", SortableItems.ITEM]).length
    } loaded-categories-${
      this.$_.filter(this.sortElems, ["type", SortableItems.CATEGORY]).length
    } ${
      !this.sortableElement.isFullyLoaded && this.sortableElement.offset > 0
        ? `button-index-${
            this.$_.filter(this.sortElems, ["type", SortableItems.ITEM]).length
          }`
        : ""
    }`;
  }

  public get subcategories() {
    return this.$_.filter(
      this.sortElems,
      sortElem => sortElem.type === SortableItems.CATEGORY
    );
  }

  public get items() {
    return this.$_.filter(
      this.sortElems,
      sortElem => sortElem.type === SortableItems.ITEM
    );
  }

  public async createNewSubcategory(parentCategoryId: IProductCategory["id"]) {
    this.createSubcategory(parentCategoryId);
  }

  public async createNewItem(parentCategoryId: IProductCategory["id"]) {
    try {
      const result = await this.createItem(parentCategoryId);

      if (result.cancelled) {
        return;
      }

      const productId: IProduct["id"] = this.$_.get(result, "results.id");
      const categoryId: IProductCategory["id"] = this.$_.get(
        result,
        "results.category_id"
      );

      await this.$store.commit(`${this.sortableCategoriesModule}/addNewItem`, {
        scope: this.scope,
        categoryId,
        data: this.$_.merge(this.$_.pick(result.results, ["name", "order"]), {
          id: `${SortableItems.ITEM}_${productId}`,
          itemId: productId
        })
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async viewElement(element) {
    try {
      const result = await this.view(element);

      if (result.cancelled) {
        return;
      }

      if (
        this.$_.get(element, "name") !== this.$_.get(result, "results.name")
      ) {
        await this.updateCategoryName(element.id, {
          name: this.$_.get(result, "results.name"),
          isLoading: false
        });
      }

      if (
        this.$_.get(result, "results.parent_id") !==
        this.$_.get(
          element,
          `parentCategories[${this.$_.get(element, "parentCategories", [])
            .length - 1}].id`,
          null
        )
      ) {
        await this.moveCategory(this.sortableElement.id, {
          parentCategoryId: this.$_.get(result, "results.parent_id"),
          newCategoryOrder: this.$_.get(result, "results.order")
        });
      }
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public updateCategoryName(categoryId, data) {
    return this.$store.commit(
      `${this.sortableCategoriesModule}/updateCategoryName`,
      {
        scope: this.scope,
        categoryId,
        data
      }
    );
  }

  public moveCategory(categoryId, data) {
    return this.$store.commit(`${this.sortableCategoriesModule}/moveCategory`, {
      scope: this.scope,
      categoryId,
      data
    });
  }

  public duplicateElement(params) {
    if (!this.can.duplicateItem) return;
    this.duplicateItem(params);
  }

  public async deleteElement(id: string, type: string, itemId = null) {
    const result = await this.confirmDelete(
      type === SortableItems.ITEM ? itemId : id,
      type
    );

    if (!this.$_.get(result, "deleteConfirmed")) {
      return;
    }

    if (type === SortableItems.ITEM) {
      await this.deleteItem(this.sortableElement.id, { id });
      return;
    }

    await this.deleteCategory(this.sortableElement.id);
  }

  public get can(): ISortableCan {
    return {
      reorder: !!this.reorder && !this.isDisabled,
      detach: !!this.confirmDetach && !this.isDisabled,
      createSubcategory: !!this.createSubcategory && !this.isDisabled,
      createItem: !!this.createItem && !this.isDisabled,
      view: !!this.view,
      delete: !!this.confirmDelete && !this.isDisabled,
      select: this.isSelectable && !this.isDisabled,
      click: this.isItemClickable,
      duplicateItem: !!this.duplicateItem && !this.isDisabled
    };
  }

  public get loadMoreLabel() {
    return this.$t("_.load_more_of", {
      total: this.sortableElement.leftToLoad,
      left:
        this.sortableElement.leftToLoad <= this.limit
          ? this.sortableElement.leftToLoad
          : this.limit
    });
  }

  public async detachElement(id: string, type: string) {
    this.confirmDetach(id, type);
  }

  public async selectElement(item, isSelected) {
    this.$emit("selectElement", item, isSelected);
  }

  public async openCloseCategory(categoryId, isOpen, offset) {
    this.$emit("openCloseCategory", categoryId, isOpen, offset);
  }

  public async loadNewItems(limit: null | number = null) {
    if (!this.loadItems) return;

    this.openCloseCategory(
      this.sortableElement.id,
      this.sortableElement.isOpened,
      limit === 0
        ? this.sortableElement.itemsTotal
        : this.sortableElement.offset + this.limit
    );

    const results = await this.loadItems(
      this.sortableElement.id,
      this.sortableElement.offset,
      limit === 0 ? this.sortableElement.itemsTotal : null
    );

    this.$store.commit(`${this.sortableCategoriesModule}/updateCategoryItems`, {
      scope: this.scope,
      categoryId: this.sortableElement.id,
      data: {
        items: results.items,
        total: results.total
      }
    });
  }

  public loadMore(limit: null | number = null) {
    if (!this.loadItems) return;

    this.$store.commit(`${this.sortableCategoriesModule}/updateCategory`, {
      scope: this.scope,
      categoryId: this.sortableElement.id,
      data: {
        isLoading: limit === 0 ? undefined : !this.sortableElement.isLoading,
        isLoadingAll:
          limit === 0 ? !this.sortableElement.isLoadingAll : undefined
      }
    });

    this.loadNewItems(limit);
  }

  @Watch("sortableElement.items", { immediate: true, deep: true })
  public onItemsChanged() {
    this.sortElems = this.$_.cloneDeep(this.sortableElement.items);
    this.savedOrder = this.$_.cloneDeep(this.sortElems);
  }

  @Watch("sortableElement.isOpened")
  public onIsOpenedChanged() {
    this.openCloseCategory(
      this.sortableElement.id,
      this.sortableElement.isOpened,
      this.sortableElement.offset
    );

    if (
      this.sortableElement.isOpened &&
      !this.sortableElement.isFullyLoaded &&
      this.sortableElement.isInitialising
    ) {
      this.loadNewItems();
    }
  }
}
